<!--
 * Path: packages/frontend/src/features/browser/forms/icon/Icon.vue
 * Task: Wrapper for icon fields handling edit modes and default fallbacks.
-->
<template>
  <FormBlockWrapper
    :is-editing="isEditing"
    @update:is-editing="handleToggleEdit"
    :title="labelValue"
    :info="infoValue"
    :can-edit="canEdit"
    :disable-edit="field?.readonly"
    :is-saving="uiStore.loading.save"
    @save="saveChanges"
    @show-info="$emit('click:info')"
  >
    <template #read>
      <IconRead :model-value="iconProxy" :default-icon="defaultIcon" :entity-color="entityColor" />
    </template>
    <template #edit>
      <IconEdit v-model="iconProxy" :default-icon="defaultIcon" :entity-color="entityColor" />
    </template>
  </FormBlockWrapper>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue';
import { useWorkspaceStore } from '@/store/workspace';
import { useUiStore } from '@/store/ui';
import { useEntityOperations } from '@/core/composables/useEntityOperations';
import { usePermissions } from '@/core/composables/usePermissions';
import { FormContextKey } from '@/core/keys';
import { useAppStyle } from '@/core/composables/useAppStyle';
import FormBlockWrapper from '@/core/components/forms/FormBlockWrapper.vue';
import IconRead from './IconRead.vue';
import IconEdit from './IconEdit.vue';

interface FieldDef {
  id: string;
  name: string;
  info?: string;
  readonly?: boolean;
}

interface Props {
  field?: FieldDef;
  label?: string; // Fallbacks for direct usage without field schema
  info?: string;
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false
});

const emit = defineEmits<{ (e: 'click:info'): void; }>();

const baseModelValue = defineModel<string>({ default: '' });

const workspaceStore = useWorkspaceStore();
const uiStore = useUiStore();
const { saveEntity } = useEntityOperations();
const { canEditWorkspace } = usePermissions();
const { getEntityStyle, resolveHexColor } = useAppStyle();
const formContext = inject(FormContextKey, null);

const isEditing = ref(false);
const canEdit = computed(() => canEditWorkspace.value && !props.disabled);

const activeEntity = computed(() => formContext ? formContext.entity.value : workspaceStore.selectedEntity);

const labelValue = computed(() => props.field ? props.field.name : (props.label || ''));
const infoValue = computed(() => props.field ? props.field.info : (props.info || ''));

// Calculate fallback icon and colors based on the entity type definitions
const typeStyle = computed(() => getEntityStyle(activeEntity.value?.entityType || ''));
const defaultIcon = computed(() => typeStyle.value.icon || 'mdi-shape');
const entityColor = computed(() => {
  const eColor = activeEntity.value?.color;
  return eColor ? resolveHexColor(eColor) : resolveHexColor(typeStyle.value.color || 'grey');
});

// --- Edit Logic ---
const handleToggleEdit = (newVal: boolean) => {
  if (!newVal && props.field) {
    workspaceStore.resetSelectedEntity(formContext?.isSecondary || false);
  }
  isEditing.value = newVal;
};

const saveChanges = async () => {
  if (props.field) {
    await saveEntity(formContext?.isSecondary || false);
  }
  isEditing.value = false;
};

const iconProxy = computed<string>({
  get: () => {
    if (props.field) {
      return activeEntity.value ? ((activeEntity.value as any)[props.field.id] || '') : '';
    }
    return baseModelValue.value;
  },
  set: (val: string) => {
    if (props.field) {
      if (activeEntity.value) (activeEntity.value as any)[props.field.id] = val;
    } else {
      baseModelValue.value = val;
    }
  }
});
</script>
